/**
 * @file src/input/midi/midiControlSettings.js
 * @description The two preferences that govern how a MIDI controller behaves — TAKEOVER (what a
 * fader does the first time it is moved after it started addressing a different parameter) and
 * AUTOFOCUS (whether moving a mapped control switches to that control's dimension).
 *
 * Both describe how somebody's HARDWARE behaves, so there is one value each per person: the same
 * controller should not act differently because a different orbiter is open, or because they sat
 * down at a different machine. They live in the signed-in user's `usage` settings, beside
 * `performanceMode` and `audioBuffer`, and follow the pattern `config/performanceMode.js` set:
 *
 *   1. the account (`usage.midiTakeover` / `usage.midiAutofocus`) — follows the person everywhere;
 *   2. localStorage — so a guest keeps their choice, and so a cold boot behaves before the account
 *      lands.
 *
 * They are deliberately NOT stored with the MIDI mappings. That store is keyed by ENTITY, so one
 * per-person value would have to be reconstructed from whichever orbiter's row happened to be
 * written last — a rule to remember rather than a fact to read.
 *
 * Both getters are on the HOT MIDI path (every CC message asks for the mode; every mapped write
 * asks about autofocus), so they read a module-level variable and never touch storage or the
 * network.
 */
import { fetchUserSettings, saveUserSettings } from '../../api/settingsApi.js';
import { configurationEpoch, isConfigurationEpochCurrent } from '../../config/configurationPack.js';
import { coerceTakeoverMode, DEFAULT_TAKEOVER_MODE, isTakeoverMode } from './takeoverModes.js';

const TAKEOVER_STORAGE_KEY = 'orbiters:midi-takeover';
/** The key autofocus has always used. Kept verbatim so nobody's existing choice is lost. */
const AUTOFOCUS_STORAGE_KEY = 'orbiters:midi-autofocus';
/** Set once autofocus's pre-account choice has been handed up to the account. */
const AUTOFOCUS_MIGRATED_KEY = 'orbiters:midi-autofocus-migrated';

/** Autofocus is on unless someone turns it off — it is the behaviour people already had. */
const DEFAULT_AUTOFOCUS = true;

let takeover = DEFAULT_TAKEOVER_MODE;
let autofocus = DEFAULT_AUTOFOCUS;
/**
 * Whether each preference has been settled by an authority — the account answering, or the person
 * clicking the control. A click counts because it IS the account's next value, so a hydration that
 * lands a moment later must not undo a choice somebody just made.
 */
let takeoverSettled = false;
let autofocusSettled = false;
let hydration = null;
/**
 * Whether a DIFFERENT person has been here in this page already.
 *
 * It gates the autofocus hand-up below, which is a one-time migration of a choice made before the
 * account could hold one. That migration is only ever honest for the FIRST account on a device: the
 * storage key has no uid in it, so after a switch the value it would hand up belongs to whoever was
 * here before — and hydration would write their choice into this person's account.
 */
let identityChangedHere = false;

const listeners = new Set();

function notify() {
  listeners.forEach((listener) => {
    try {
      listener();
    } catch (error) {
      console.warn('[midiControlSettings] listener failed:', error);
    }
  });
}

function readStored(key) {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return null;
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeStored(key, value) {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return;
    window.localStorage.setItem(key, value);
  } catch {
    // Persistence is best-effort; the in-memory value is what the session runs on.
  }
}

// Seeded from this device at module load so the very first MIDI message already behaves the way the
// person last chose, without waiting on a request. The account corrects it if the two differ.
const storedTakeover = readStored(TAKEOVER_STORAGE_KEY);
if (isTakeoverMode(storedTakeover)) takeover = storedTakeover;
const storedAutofocus = readStored(AUTOFOCUS_STORAGE_KEY);
if (storedAutofocus !== null) autofocus = storedAutofocus === 'true';

/** The takeover mode in force. O(1) — safe on every MIDI message. */
export function getTakeoverMode() {
  return takeover;
}

/** Whether MIDI autofocus is on. O(1) — safe on every mapped MIDI write. */
export function isAutofocusEnabled() {
  return autofocus;
}

/**
 * Choose the takeover mode. It takes effect immediately, is remembered on this device, and is saved
 * to the account fire-and-forget — the next fader move must obey the choice, not the round trip.
 * @param {string} value
 * @returns {string} the mode now in force.
 */
export function setTakeoverMode(value) {
  const next = coerceTakeoverMode(value);
  // The latch goes BELOW the equality check. Above it, choosing the value already in force would
  // mark the preference settled without persisting anything — hydration would then be blocked from
  // applying whatever the account actually says, and the next boot would silently revert it.
  if (next === takeover) return takeover;
  takeoverSettled = true;
  takeover = next;
  writeStored(TAKEOVER_STORAGE_KEY, next);
  void saveUserSettings({ usage: { midiTakeover: next } });
  notify();
  return takeover;
}

/**
 * Turn autofocus on or off. Same contract as {@link setTakeoverMode}.
 * @param {boolean} value
 * @returns {boolean} the state now in force.
 */
export function setAutofocusEnabled(value) {
  const next = Boolean(value);
  writeStored(AUTOFOCUS_STORAGE_KEY, String(next));
  if (next === autofocus) return autofocus;
  autofocusSettled = true;
  autofocus = next;
  // The migration marker is retired only once the account has actually taken the value — see the
  // note in `hydrateMidiControlSettings`. `saveUserSettings` answers null instead of throwing when
  // the write fails, so a discarded result would retire it on a save that never happened.
  void saveUserSettings({ usage: { midiAutofocus: next } }).then((saved) => {
    if (saved) writeStored(AUTOFOCUS_MIGRATED_KEY, 'true');
  });
  notify();
  return autofocus;
}

/**
 * Apply the signed-in user's saved preferences. Called once at boot; deduped, so a second caller
 * shares the first request. A guest — or a failed read — simply keeps whatever this device knew.
 *
 * AUTOFOCUS CARRIES A ONE-TIME HAND-UP. It lived in localStorage before it lived on the account,
 * and the account's default is ON, so taking the account's answer at face value would silently
 * switch autofocus back on for somebody who had deliberately turned it off on this device. The
 * first hydration on a device holding a pre-account choice therefore pushes that choice UP instead
 * of reading one down, and a marker retires the migration. Takeover has no such history — it is
 * new, so the account is simply right about it.
 *
 * @param {{ signal?: AbortSignal }} [options]
 * @returns {Promise<void>}
 */
export function hydrateMidiControlSettings({ signal } = {}) {
  if (hydration) return hydration;
  hydration = (async () => {
    // Whose preferences these are, settled before the request. A read in flight when somebody else
    // arrives would otherwise land afterwards and set both latches for the person who left — after
    // which the arriving account's own hydration, the newer question, refuses to apply its answer.
    const askedFor = configurationEpoch();
    const settings = await fetchUserSettings({ signal });
    const usage = settings?.usage;
    if (!usage || !isConfigurationEpochCurrent(askedFor)) return;

    if (!takeoverSettled) {
      const value = usage.midiTakeover;
      if (isTakeoverMode(value) && value !== takeover) {
        takeover = value;
        writeStored(TAKEOVER_STORAGE_KEY, value);
        notify();
      }
      takeoverSettled = true;
    }

    if (!autofocusSettled) {
      const local = readStored(AUTOFOCUS_STORAGE_KEY);
      const migrated = readStored(AUTOFOCUS_MIGRATED_KEY) === 'true';
      // `identityChangedHere` is what keeps the hand-up from crossing accounts. The device value it
      // would send up is not necessarily this person's — the key is device-global — so once a
      // second identity has arrived in this page the account is simply believed instead.
      if (local !== null && !migrated && !identityChangedHere) {
        // This device chose before the account could hold the answer — hand it up, don't overwrite.
        // The marker is written only if the account TOOK it: `saveUserSettings` returns null rather
        // than throwing on a failed write, so retiring the migration up front would mean one
        // offline boot is enough to lose the choice — the account keeps its ON default and the
        // next boot reads it down, which is the exact flip this migration exists to prevent.
        const saved = await saveUserSettings({ usage: { midiAutofocus: autofocus } });
        // Somebody else arrived while that write was out: the marker, and the latch below, would be
        // about the person who has left.
        if (!isConfigurationEpochCurrent(askedFor)) return;
        if (saved) writeStored(AUTOFOCUS_MIGRATED_KEY, 'true');
      } else if (typeof usage.midiAutofocus === 'boolean' && usage.midiAutofocus !== autofocus) {
        autofocus = usage.midiAutofocus;
        writeStored(AUTOFOCUS_STORAGE_KEY, String(autofocus));
        notify();
      }
      autofocusSettled = true;
    }
  })().catch(() => {
    // A settings read that fails leaves this device's values in place, which is the safe answer.
  });
  return hydration;
}

/**
 * Somebody else is here now.
 *
 * Both preferences are per PERSON and both are remembered per DEVICE, with module-level latches
 * that make a settled preference final for the life of the page. So a sign-in after a sign-out kept
 * the previous person's takeover mode and autofocus, and the arriving account was never consulted.
 *
 * The latches are dropped so the next hydration actually asks; the values in force are left alone,
 * because they are on the hot MIDI path and flipping them to defaults at a moment nobody asked for
 * anything would be its own surprise. And the autofocus hand-up is closed for good on this page —
 * see `identityChangedHere`.
 *
 * Called from both identity edges in `src/auth/sessionAuth.js`.
 */
export function resetMidiControlSettingsForIdentityChange() {
  identityChangedHere = true;
  takeoverSettled = false;
  autofocusSettled = false;
  hydration = null;
}

/**
 * Subscribe to changes to either preference. Returns an unsubscribe.
 * Shaped for `useSyncExternalStore`.
 * @param {() => void} listener
 * @returns {() => void}
 */
export function subscribeMidiControlSettings(listener) {
  if (typeof listener !== 'function') return () => {};
  listeners.add(listener);
  return () => listeners.delete(listener);
}
